import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText as MuiListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  Shield,
  Lock,
  VpnKey,
  Security,
  Add,
} from '@mui/icons-material';
import {
  useSuperadminRoles,
  useSuperadminPermissions,
  useCreateSuperadminRole,
  useUpdateSuperadminRole,
  useDeleteSuperadminRole,
} from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { colors, transitions, radius } from '../../theme/theme';

export const RolesPage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { roles, isLoading, error } = useSuperadminRoles();
  const { permissions: availablePermissions } = useSuperadminPermissions();
  const createRole = useCreateSuperadminRole();
  const updateRole = useUpdateSuperadminRole();
  const deleteRole = useDeleteSuperadminRole();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    guard_name: 'web',
    permissions: [] as number[],
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, role: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRole(null);
  };

  const handleAddRole = () => {
    setFormData({
      name: '',
      guard_name: 'web',
      permissions: [],
    });
    setAddDialogOpen(true);
  };

  const handleEditRole = () => {
    if (!selectedRole) return;
    setFormData({
      name: selectedRole.name || '',
      guard_name: selectedRole.guard_name || 'web',
      permissions: selectedRole.permissions?.map((p: any) => p.id) || [],
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;

    if (window.confirm(`Are you sure you want to delete the role "${selectedRole.name}"?`)) {
      try {
        await deleteRole.mutateAsync(selectedRole.id);
        showSnackbar('Role deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-roles'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete role', 'error');
      }
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitAdd = async () => {
    try {
      await createRole.mutateAsync(formData);
      showSnackbar('Role created successfully', 'success');
      setAddDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-roles'] });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to create role';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedRole) return;

    try {
      await updateRole.mutateAsync({ id: selectedRole.id, data: formData });
      showSnackbar('Role updated successfully', 'success');
      setEditDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-roles'] });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to update role';
      showSnackbar(errorMessage, 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Shield sx={{ fontSize: 18, color: colors.secondary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600 }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
              Guard: {params.row.guard_name || 'web'}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'guard_name',
      headerName: 'Guard',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={<Lock sx={{ fontSize: 14 }} />}
          label={params.value || 'web'}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: 'permissions',
      headerName: 'Permissions',
      width: 150,
      valueGetter: (value: any) => Array.isArray(value) ? value.length : 0,
      renderCell: (params) => (
        <Chip
          icon={<VpnKey sx={{ fontSize: 14 }} />}
          label={`${params.value} permissions`}
          size="small"
          color="secondary"
        />
      ),
    },
    {
      field: 'users_count',
      headerName: 'Users',
      width: 100,
      valueGetter: (value) => value || 0,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Superadmin Roles"
          subtitle="Manage superadmin role permissions"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'User Management' },
            { label: 'Superadmin Roles' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddRole}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Role
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load superadmin roles. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && roles && Array.isArray(roles) && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.secondary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Roles</Box>
                    <Shield sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {roles.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Superadmin roles</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.primary,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.primary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Permissions</Box>
                    <VpnKey sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {roles.reduce((sum: number, role: any) => sum + (role.permissions?.length || 0), 0)}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Across all roles</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.success,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.success.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>With Users</Box>
                    <Security sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {roles.filter((r: any) => (r.users_count || 0) > 0).length}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Roles with assigned users</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.info,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.info.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Avg Permissions</Box>
                    <Lock sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {roles.length > 0
                      ? Math.round(
                          roles.reduce((sum: number, role: any) => sum + (role.permissions?.length || 0), 0) /
                            roles.length
                        )
                      : 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Per role</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'visible',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: colors.shadows.base,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={Array.isArray(roles) ? roles : []}
                  columns={columns}
                  autoHeight
                  pageSizeOptions={[10, 25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    borderRadius: 2,
                    '& .MuiDataGrid-main': {
                      borderRadius: 2,
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      borderTopLeftRadius: '16px',
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-columnHeader:first-of-type': {
                      borderTopLeftRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeader:last-of-type': {
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: transitions.fast,
                      '&:hover': {
                        backgroundColor: `${colors.secondary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                      borderBottomLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleEditRole}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Role</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteRole} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete Role</ListItemText>
          </MenuItem>
        </Menu>

        {/* Add Role Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Role</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Role Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Guard Name"
                value={formData.guard_name}
                onChange={(e) => handleFormChange('guard_name', e.target.value)}
                fullWidth
                helperText="Default: web"
              />
              <FormControl fullWidth>
                <InputLabel>Permissions</InputLabel>
                <Select
                  multiple
                  value={formData.permissions}
                  onChange={(e) => handleFormChange('permissions', e.target.value)}
                  input={<OutlinedInput label="Permissions" />}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map((id) => Array.isArray(availablePermissions) ? availablePermissions.find((p: any) => p.id === id)?.name : undefined)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {Array.isArray(availablePermissions) && availablePermissions.map((permission: any) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      <Checkbox checked={formData.permissions.indexOf(permission.id) > -1} />
                      <MuiListItemText primary={permission.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitAdd}
              variant="contained"
              disabled={createRole.isPending}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                },
              }}
            >
              {createRole.isPending ? 'Creating...' : 'Create Role'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Role</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Role Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Guard Name"
                value={formData.guard_name}
                onChange={(e) => handleFormChange('guard_name', e.target.value)}
                fullWidth
                helperText="Default: web"
              />
              <FormControl fullWidth>
                <InputLabel>Permissions</InputLabel>
                <Select
                  multiple
                  value={formData.permissions}
                  onChange={(e) => handleFormChange('permissions', e.target.value)}
                  input={<OutlinedInput label="Permissions" />}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map((id) => Array.isArray(availablePermissions) ? availablePermissions.find((p: any) => p.id === id)?.name : undefined)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {Array.isArray(availablePermissions) && availablePermissions.map((permission: any) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      <Checkbox checked={formData.permissions.indexOf(permission.id) > -1} />
                      <MuiListItemText primary={permission.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitEdit}
              variant="contained"
              disabled={updateRole.isPending}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                },
              }}
            >
              {updateRole.isPending ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
